import React, { useState } from 'react';
import { ArrowRight, BookOpen, Award, Play, Search, X } from 'lucide-react';
import { G1, GG, NavyBg, PAGE_ROOT, OR, OG } from '../../config/styles';
import { Spin } from '../shared';

const ACCENT  = '#fb923c';   // برتقالي الأكاديمية
const ACCENT2 = '#f97316';

export default function Instructors({
  instructors = [], courses = [], lessons = [], enrollments = [], customer,
  lang, isRTL, loading, t, onSetPage, focusInstructorId, clearFocus, embedded = false,
}) {
  const ar = lang === 'ar';
  const [selectedId, setSelectedId] = useState(focusInstructorId || null);
  const [query, setQuery] = useState('');

  React.useEffect(() => {
    if (focusInstructorId) setSelectedId(focusInstructorId);
  }, [focusInstructorId]);

  const active = [...instructors].filter(i => i.is_active !== false).sort((a, b) => (a.order_num || 0) - (b.order_num || 0));
  const q = query.trim().toLowerCase();
  const shown = q
    ? active.filter(i => `${i.name_ar || ''} ${i.name_en || ''} ${i.title_ar || ''} ${i.specialties || ''}`.toLowerCase().includes(q))
    : active;
  const selected = selectedId ? instructors.find(i => i.id === selectedId) : null;

  const instructorCourses = (instId) => courses.filter(c => c.instructor_id === instId);
  const cLessons = (cid) => lessons.filter(l => l.course_id === cid);
  const isEnrolled = (cid) => enrollments.some(e => e.customer_id === customer?.id && e.course_id === cid && e.status === 'active');

  // غلاف الصفحة: خلفية كحلي بس لما تكون مستقلة (مش مدمجة جوّه الأكاديمية)
  const Shell = ({ children }) => (
    embedded
      ? <div style={{ fontFamily: "'Tajawal', sans-serif" }}>{children}</div>
      : <div style={PAGE_ROOT}><NavyBg />{children}</div>
  );

  if (loading) return <Shell><Spin /></Shell>;

  // ───────── صفحة مدرب واحد ─────────
  if (selected) {
    const myCourses = instructorCourses(selected.id);
    const experiences = (selected.experience_ar || '').split('\n').map(s => s.trim()).filter(Boolean);
    return (
      <Shell>
        <div style={{ maxWidth: '960px', margin: '0 auto', paddingBottom: '40px' }}>
          {/* رجوع */}
          <button onClick={() => { setSelectedId(null); clearFocus && clearFocus(); }}
            style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: '10px', padding: '8px 14px', color: 'white', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '18px', fontWeight: 'bold' }}>
            <ArrowRight size={15} style={{ transform: isRTL ? 'none' : 'rotate(180deg)' }} /> {t.allInstructors}
          </button>

          {/* كارت المدرب */}
          <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '22px', overflow: 'hidden', boxShadow: '0 18px 44px rgba(0,0,0,0.32)', marginBottom: '22px', animation: 'cardRise .5s ease both' }}>
            <div style={{ background: OG, height: '96px' }} />
            <div style={{ padding: '0 26px 26px', marginTop: '-50px' }}>
              {selected.avatar_url
                ? <img src={selected.avatar_url} alt={selected.name_ar} style={{ width: '104px', height: '104px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #131a3f', boxShadow: '0 6px 18px rgba(0,0,0,0.35)' }} />
                : <div style={{ width: '104px', height: '104px', borderRadius: '50%', background: 'rgba(249,115,22,0.18)', border: '4px solid #131a3f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '42px' }}>👨‍🏫</div>}
              <h1 style={{ fontSize: '27px', fontWeight: 800, color: '#fff', margin: '14px 0 4px' }}>{ar ? selected.name_ar : (selected.name_en || selected.name_ar)}</h1>
              {selected.title_ar && <div style={{ fontSize: '15px', color: ACCENT, fontWeight: 'bold', marginBottom: '8px' }}>{selected.title_ar}</div>}
              {selected.specialties && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                  {selected.specialties.split(/[،,]/).map(s => s.trim()).filter(Boolean).map((s, i) => (
                    <span key={i} style={{ fontSize: '12px', background: 'rgba(249,115,22,0.14)', color: ACCENT, padding: '4px 12px', borderRadius: '20px', fontWeight: 'bold', border: '1px solid rgba(249,115,22,0.25)' }}>{s}</span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selected.bio_ar && (
            <Section icon={<BookOpen size={18} color={ACCENT2} />} title={t.aboutLabel}>
              <p style={{ fontSize: '15px', lineHeight: 1.9, color: 'rgba(255,255,255,0.8)', margin: 0, whiteSpace: 'pre-line' }}>{selected.bio_ar}</p>
            </Section>
          )}

          {experiences.length > 0 && (
            <Section icon={<Award size={18} color={ACCENT2} />} title={t.experienceLabel}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {experiences.map((e, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'rgba(255,255,255,0.8)' }}>
                    <span style={{ color: '#34d399' }}>✓</span> {e}
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section icon={<Play size={18} color={ACCENT2} />} title={t.instructorCourses} right={<span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>{myCourses.length}</span>}>
            {myCourses.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>{t.noInstructorCourses}</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: '14px' }}>
                {myCourses.map(c => {
                  const cl = cLessons(c.id);
                  const enrolled = isEnrolled(c.id);
                  return (
                    <div key={c.id} style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', overflow: 'hidden', background: 'rgba(255,255,255,0.03)' }}>
                      {c.thumbnail_url
                        ? <img src={c.thumbnail_url} alt={c.name_ar} style={{ width: '100%', height: '110px', objectFit: 'cover' }} />
                        : <div style={{ height: '110px', background: OG, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px' }}>{c.icon}</div>}
                      <div style={{ padding: '12px' }}>
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#fff', marginBottom: '8px' }}>{ar ? c.name_ar : c.name_en}</div>
                        <button onClick={() => onSetPage('player', c, cl[0] || null)}
                          style={{ width: '100%', padding: '8px', background: enrolled ? GG : OG, color: 'white', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer' }}>
                          {enrolled ? (t.watchBtn) : (t.detailsBtn)}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>
      </Shell>
    );
  }

  // ───────── قائمة كل المدربين ─────────
  return (
    <Shell>
      {!embedded && (
        <div style={{ textAlign: 'center', color: 'white', marginBottom: '30px', animation: 'fadeInDown .6s ease both' }}>
          <h2 style={{ fontSize: '40px', fontWeight: 'bold', textShadow: '2px 2px 12px rgba(0,0,0,0.25)' }}>👨‍🏫 {t.instructorsTitle}</h2>
          <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: '15px', marginTop: '6px' }}>{active.length} {t.instructorsCount}</p>
        </div>
      )}

      {/* ── شريط البحث ── */}
      {active.length > 0 && (
        <div style={{ maxWidth: '560px', margin: '0 auto 26px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} color="#9CA3AF" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', insetInlineStart: '14px', pointerEvents: 'none' }} />
            <input
              value={query} onChange={e => setQuery(e.target.value)}
              placeholder={t.searchInstructor}
              style={{ width: '100%', padding: '13px 44px', borderRadius: '14px', border: 'none', fontSize: '14px', boxShadow: '0 6px 18px rgba(0,0,0,0.18)', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            {query && (
              <button onClick={() => setQuery('')} style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', insetInlineEnd: '12px', background: '#F3F4F6', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={13} color="#6B7280" />
              </button>
            )}
          </div>
        </div>
      )}

      {active.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'white' }}>
          <div style={{ fontSize: '54px', marginBottom: '12px', opacity: 0.6 }}>👨‍🏫</div>
          <div style={{ fontSize: '16px', opacity: 0.85 }}>{t.noInstructors}</div>
        </div>
      ) : shown.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: 'white' }}>
          <div style={{ fontSize: '46px', marginBottom: '10px', opacity: 0.6 }}>🔍</div>
          <div style={{ fontSize: '15px', opacity: 0.85 }}>{t.noInstructorMatch}</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: '20px' }}>
          {shown.map((inst, index) => {
            const count = instructorCourses(inst.id).length;
            return (
              <div key={inst.id} style={{ animation: 'cardRise .55s cubic-bezier(.22,1,.36,1) both', animationDelay: `${(index % 12) * 0.07}s` }}>
                <div onClick={() => { setSelectedId(inst.id); window.scrollTo(0, 0); }}
                  className="gw-card"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '18px', padding: '24px', textAlign: 'center', cursor: 'pointer', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
                  {inst.avatar_url
                    ? <img src={inst.avatar_url} alt={inst.name_ar} style={{ width: '92px', height: '92px', borderRadius: '50%', objectFit: 'cover', margin: '0 auto', border: `3px solid ${ACCENT2}` }} />
                    : <div style={{ width: '92px', height: '92px', borderRadius: '50%', background: OG, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px' }}>👨‍🏫</div>}
                  <h3 style={{ fontSize: '17px', fontWeight: 'bold', color: '#fff', margin: '14px 0 4px' }}>{ar ? inst.name_ar : (inst.name_en || inst.name_ar)}</h3>
                  {inst.title_ar && <div style={{ fontSize: '13px', color: ACCENT, fontWeight: 'bold' }}>{inst.title_ar}</div>}
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                    <BookOpen size={13} /> {count} {t.courseWord}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!embedded && (
        <div style={{ textAlign: 'center', marginTop: '32px' }}>
          <button onClick={() => onSetPage('home')} style={{ padding: '11px 28px', background: 'rgba(255,255,255,0.95)', color: OR, border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}>{t.backHome}</button>
        </div>
      )}
    </Shell>
  );
}

function Section({ icon, title, right, children }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '18px', padding: '20px 22px', marginBottom: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.28)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '16px' }}>
        {icon}
        <h2 style={{ fontSize: '18px', fontWeight: 800, color: '#fff', margin: 0, flex: 1 }}>{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}
